import { productRowRange, type AmountRange } from '../../../../backend/src/utils/quoteTotal';
import { useCallback, useState, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { Package, Plus } from 'lucide-react';
import { uboTheme, themeColors } from '@/theme/uboTheme';
import { useQuotePageBrand } from '@/store/quoteClientLogoContext';
import type { PricingType } from '@/theme/uboTheme';
import type { Node as PMNode } from '@tiptap/pm/model';
import {
  formatCurrency,
  formatCurrencyRange,
  isCurrencyAmount,
  parseCurrencyAmount,
} from '@/lib/format';
import { emptyTableRowMatchingTable } from '@/editor/investmentTableRow';
import { investmentRowAmount } from '@/editor/investmentRowAmount';
import { buildInvestmentOverviewAfter } from '@/editor/investmentPhaseActions';
import { useActiveEditor } from '@/store/editorContext';
import { useQuoteDocumentEditable, useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import {
  getInvestmentTableLabels,
  normalizeInvestmentLabelLanguage,
} from '@/editor/investmentTableLabels';

/** Gebruikt documentpositie i.p.v. node-referentie (PM-nodes vernieuwen per transactie). */
function findPrecedingTable(
  editor: NodeViewProps['editor'],
  selfPos: number | undefined
): PMNode | null {
  if (!editor || selfPos == null) return null;
  const doc = editor.state.doc;
  const at = doc.nodeAt(selfPos);
  if (!at || at.type.name !== 'investmentTotalNode') return null;
  const $pos = doc.resolve(selfPos);
  for (let d = $pos.depth; d >= 1; d--) {
    const parent = $pos.node(d);
    if (parent.type.name !== 'investmentGroupNode') continue;
    const childIndex = $pos.index(d);
    let lastTable: PMNode | null = null;
    for (let i = 0; i < childIndex; i++) {
      const c = parent.child(i);
      if (c.type.name === 'table') lastTable = c;
    }
    return lastTable;
  }
  return null;
}

function findPrecedingTablePos(
  editor: NodeViewProps['editor'],
  selfPos: number | undefined
): { from: number; to: number } | null {
  if (!editor || selfPos == null) return null;
  const doc = editor.state.doc;
  const at = doc.nodeAt(selfPos);
  if (!at || at.type.name !== 'investmentTotalNode') return null;
  const $pos = doc.resolve(selfPos);
  for (let d = $pos.depth; d >= 1; d--) {
    const parent = $pos.node(d);
    if (parent.type.name !== 'investmentGroupNode') continue;
    const groupStart = $pos.before(d);
    const childIndex = $pos.index(d);
    let childPos = groupStart + 1;
    let lastTable: PMNode | null = null;
    let lastFrom = -1;
    for (let i = 0; i < childIndex; i++) {
      const c = parent.child(i);
      if (c.type.name === 'table') {
        lastTable = c;
        lastFrom = childPos;
      }
      childPos += c.nodeSize;
    }
    if (lastTable && lastFrom >= 0) {
      return { from: lastFrom, to: lastFrom + lastTable.nodeSize };
    }
    return null;
  }
  return null;
}

export default function InvestmentTotalView({
  node,
  editor,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const isEditable = useQuoteDocumentMode() === 'edit';
  const canEditText = useQuoteDocumentEditable();
  const { setProductPickerTableId } = useActiveEditor();
  const pageBrand = useQuotePageBrand();
  const attrs = node.attrs as Record<string, unknown>;
  const pricingType = (attrs.pricingType as PricingType) || 'fixed';
  const label = (attrs.label as string) || 'Totaal excl. BTW';
  const isHourly = pricingType === 'hourly';
  const isFixed = pricingType === 'fixed';
  const fixedTotal = (attrs.fixedTotal as number) ?? 0;
  const fixedTotalText = typeof attrs.fixedTotalText === 'string' ? attrs.fixedTotalText : '';
  const showTotalRow = attrs.showTotalRow !== false;
  const [localFixedTotal, setLocalFixedTotal] = useState(
    fixedTotalText || (fixedTotal ? formatCurrency(fixedTotal, 'EUR') : '')
  );

  const [total, setTotal] = useState<AmountRange>({ min: 0, max: 0 });
  const [hasFixedProducts, setHasFixedProducts] = useState(false);

  const readGroupTheme = useCallback((): 'ubo' | 'ubx' => {
    if (!editor || typeof getPos !== 'function') return pageBrand;
    const pos = getPos();
    if (pos == null) return pageBrand;
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        const theme = n.attrs.theme as string | undefined;
        return theme === 'ubx' || theme === 'ubo' ? theme : pageBrand;
      }
    }
    return pageBrand;
  }, [editor, getPos, pageBrand]);

  const [effectiveTheme, setEffectiveTheme] = useState<'ubo' | 'ubx'>(pageBrand);
  useEffect(() => {
    setEffectiveTheme(readGroupTheme());
  }, [readGroupTheme]);

  const readGroupCurrency = useCallback((): string => {
    if (!editor || typeof getPos !== 'function') return 'EUR';
    const pos = getPos();
    if (pos == null) return 'EUR';
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        return (n.attrs.currency as string) || 'EUR';
      }
    }
    return 'EUR';
  }, [editor, getPos]);

  const readGroupLabelLanguage = useCallback(() => {
    if (!editor || typeof getPos !== 'function') return 'nl' as const;
    const pos = getPos();
    if (pos == null) return 'nl' as const;
    const $pos = editor.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        return normalizeInvestmentLabelLanguage(n.attrs.labelLanguage);
      }
    }
    return 'nl' as const;
  }, [editor, getPos]);

  const [currency, setCurrency] = useState('EUR');
  useEffect(() => {
    setCurrency(readGroupCurrency());
  }, [readGroupCurrency]);

  useEffect(() => {
    setLocalFixedTotal(fixedTotalText || (fixedTotal ? formatCurrency(fixedTotal, currency) : ''));
  }, [fixedTotal, fixedTotalText, currency]);

  useEffect(() => {
    if (!editor) return;
    const fn = () => {
      setCurrency(readGroupCurrency());
      setEffectiveTheme(readGroupTheme());
    };
    editor.on('transaction', fn);
    return () => {
      editor.off('transaction', fn);
    };
  }, [editor, readGroupCurrency, readGroupTheme]);

  useEffect(() => {
    if (!editor) return;

    const recalc = () => {
      const selfPos = typeof getPos === 'function' ? getPos() : undefined;
      const prevTable = findPrecedingTable(editor, selfPos);

      // Van–tot prijzen maken van het totaal een bandbreedte; zelfde regel als de klantpagina.
      const productRange = (row: PMNode): AmountRange | null => productRowRange(row.attrs);

      if (isFixed) {
        const sum = { min: fixedTotal || 0, max: fixedTotal || 0 };
        let hasProducts = false;
        prevTable?.forEach((row: PMNode) => {
          const range = productRange(row);
          if (!range) return;
          hasProducts = true;
          sum.min += range.min;
          sum.max += range.max;
        });
        setHasFixedProducts(hasProducts);
        setTotal(sum);
        return;
      }

      if (!prevTable) {
        setTotal({ min: 0, max: 0 });
        return;
      }

      const sum = { min: 0, max: 0 };
      prevTable.forEach((row: any) => {
        if (row.type.name !== 'tableRow') return;
        let hasHeader = false;
        row.forEach((cell: any) => {
          if (cell.type.name === 'tableHeader') hasHeader = true;
        });
        if (hasHeader) return;

        const cells: string[] = [];
        row.forEach((cell: any) => {
          if (cell.type.name === 'tableHeader') return;
          cells.push(cell.textContent || '0');
        });
        if (cells.length === 0) return;

        const range = productRange(row);
        const amount = range ? 0 : investmentRowAmount(cells, isHourly);
        sum.min += range ? range.min : amount;
        sum.max += range ? range.max : amount;
      });
      setTotal(sum);
    };

    recalc();
    editor.on('update', recalc);
    return () => {
      editor.off('update', recalc);
    };
  }, [editor, getPos, isHourly, isFixed, fixedTotal]);

  const handleAddRow = useCallback(() => {
    if (!editor) return;
    const selfPos = typeof getPos === 'function' ? getPos() : undefined;
    const tablePos = findPrecedingTablePos(editor, selfPos);
    if (!tablePos) return;

    const tableNode = findPrecedingTable(editor, selfPos);
    if (!tableNode) return;

    const insertPos = tablePos.to - 1;
    const row = emptyTableRowMatchingTable(tableNode, pricingType, readGroupCurrency());
    editor.chain().focus().insertContentAt(insertPos, row).run();
  }, [editor, getPos, pricingType, readGroupCurrency]);

  const handleAddOverview = useCallback(() => {
    const pos = typeof getPos === 'function' ? getPos() : undefined;
    if (pos == null) return;
    const insertion = buildInvestmentOverviewAfter(
      editor.state.doc,
      pos,
      getInvestmentTableLabels(readGroupLabelLanguage())
    );
    if (!insertion) return;
    editor.chain().focus().insertContentAt(insertion.position, insertion.content).run();
  }, [editor, getPos, readGroupLabelLanguage]);

  const handleFixedTotalChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFixedTotal(e.target.value);
  }, []);

  const handleFixedTotalBlur = useCallback(() => {
    const value = localFixedTotal.trim();
    if (value && !isCurrencyAmount(value)) {
      setLocalFixedTotal(value);
      updateAttributes({ fixedTotal: 0, fixedTotalText: value });
      return;
    }

    const num = parseCurrencyAmount(value);
    setLocalFixedTotal(value ? formatCurrency(num, currency) : '');
    updateAttributes({ fixedTotal: num, fixedTotalText: null });
  }, [currency, localFixedTotal, updateAttributes]);

  const colors = themeColors(effectiveTheme);
  const tableRule = colors.investmentTableRule ?? colors.primary;

  return (
    <NodeViewWrapper
      as="div"
      className={`investment-total-node-view${isEditable ? ' is-editable' : ''}`}
    >
      {showTotalRow && (
        <div
          className="investment-total-row"
          style={{
            borderTop: `2px solid ${tableRule}`,
            backgroundColor: colors.tableHeaderBg,
          }}
        >
          <span
            className="investment-total-label"
            style={{
              fontFamily: uboTheme.fonts.secondaryFallback,
            }}
          >
            {label}
          </span>
          {isFixed && canEditText && !hasFixedProducts ? (
            <input
              type="text"
              value={localFixedTotal}
              onChange={handleFixedTotalChange}
              onBlur={handleFixedTotalBlur}
              placeholder="Bedrag of tekst, bijv. Op maat"
              className="investment-total-value investment-total-input"
              style={{ fontFamily: uboTheme.fonts.secondaryFallback }}
            />
          ) : (
            <span
              className="investment-total-value"
              style={{ fontFamily: uboTheme.fonts.secondaryFallback }}
            >
              {isFixed
                ? (!hasFixedProducts && fixedTotalText) ||
                  formatCurrencyRange(total.min, total.max, currency)
                : formatCurrencyRange(total.min, total.max, currency)}
            </span>
          )}
        </div>
      )}

      {isFixed && hasFixedProducts && isEditable && (
        <label
          className="flex items-center justify-end gap-3 px-3 py-2 text-xs text-slate-500"
          contentEditable={false}
        >
        Vast fasebedrag naast de producten
          <input
            aria-label="Vast fasebedrag naast de producten"
            type="text"
            value={localFixedTotal}
            onChange={handleFixedTotalChange}
            onBlur={handleFixedTotalBlur}
            className="w-28 rounded border px-2 py-1 text-right"
          />
        </label>
      )}

      {isEditable ? (
        <div contentEditable={false} data-html2canvas-ignore="true" className="investment-row-add">
          <button type="button" className="investment-row-add-action" onClick={handleAddRow}>
            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Regel toevoegen
          </button>
          <button
            type="button"
            className="investment-row-add-action"
            title="Product uit catalogus toevoegen"
            onClick={() => setProductPickerTableId(String(attrs.tableId))}
          >
            <Package className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Product toevoegen
          </button>
        </div>
      ) : null}

      {isEditable ? (
        <div
          contentEditable={false}
          data-html2canvas-ignore="true"
          className="investment-overview-add"
        >
          <button type="button" className="investment-element-trigger" onClick={handleAddOverview}>
            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Investeringsoverzicht toevoegen
          </button>
        </div>
      ) : null}
    </NodeViewWrapper>
  );
}
